import { useMemo } from "react"
import { useAuth } from "../context/AuthContext"
import { can, grantsToMap, isFullAccess, hasAssignedRole } from "./can"

/**
 * What the signed-in user may do, and who they are in role terms.
 *
 * The role facts live here rather than being re-derived per screen. A screen that
 * needs "is this an admin" reaching for `profile_type_id === 2` is the classic
 * version of this bug — no TallyWise account carries that, so the test is always
 * false, and each copy of it is wrong in its own way.
 */
export function usePermissions() {
  const { user } = useAuth()

  return useMemo(() => {
    const grants = user?.grants
    const isBackOffice = String(user?.organization_id) === "0"

    return {
      user,
      can: (moduleKey, action) => can(user, moduleKey, action),
      grantMap: grantsToMap(grants),
      isSuperAdmin: isFullAccess(grants),
      /** Organization "0". Cross-tenant reach comes from the `clients` module on
       *  top of this, not from this alone. */
      isBackOffice,
      roleName: user?.role_name ?? null,
      roleLabel: user?.role_label ?? null,
      roleCategory: user?.role_category ?? null,
      persona: user?.persona ?? null,
      /** False for a role scoped to what it was assigned rather than the whole
       *  organization — it sees its own projects and tasks, not everyone's. */
      seesWholeOrganization: isFullAccess(grants) || user?.data_scope !== "assigned",
      /** An account with no role reaches nothing; every enforced route answers
       *  NO_ROLE_ASSIGNED. Worth surfacing rather than rendering an empty app. */
      hasRole: hasAssignedRole(user),
      organizationId: user?.organization_id != null ? String(user.organization_id) : null,
      organizationLabel: user?.organization_label ?? null,
    }
  }, [user])
}
